<template>
  <div>
    <sf-alert-error v-if="tokenError" class="mb-4" type="error">
      {{ $t('error.registrationTokenError') }}
    </sf-alert-error>
    <v-form v-if="!tokenError" ref="form" v-model="valid" lazy-validation>
      <sf-select
        v-model="selectedTitle"
        :items="titleItems"
        item-title="translation"
        :label="$t('user.contactTitle')"
        return-object
        autocomplete="honorific-prefix"
      />
      <sf-text-field
        v-model="firstName"
        :label="$t('user.firstName') + ' *'"
        :rules="[rules.requiredRule, rules.nameRule]"
        required
        autocomplete="given-name"
      />
      <sf-text-field
        v-model="lastName"
        :label="$t('user.lastName') + ' *'"
        :rules="[rules.requiredRule, rules.nameRule]"
        class="mb-3"
        required
        autocomplete="family-name"
      />
      <sf-password-field
        v-model="password"
        :label="$t('password') + ' *'"
        :rules="[rules.passwordLengthRule, rules.passwordRule]"
        required
        autocomplete="new-password"
      />
      <sf-password-field
        v-model="repeat"
        :label="$t('passwordRepeat') + ' *'"
        :rules="[rules.passwordMatchRule(password)]"
        required
        autocomplete="new-password"
      />
      <sf-checkbox
        ref="terms-checkbox"
        v-model="termsConsent"
        :label="$t('privacyConsent') + ' *'"
        :rules="[rules.requiredRule]"
        required
      />
      <custom-terms-checkbox
        v-if="customTerms.isCustomTerms"
        ref="custom-terms-checkbox"
        v-model="customTermsConsent"
        :custom-terms="customTerms.terms ?? ''"
      />
      <sf-large-button
        class="mt-2"
        block
        :loading="sending"
        :disabled="!valid"
        @click="sendRegistration"
      >
        {{ $t('finishRegistration') }}
      </sf-large-button>
    </v-form>
    <login-link />
  </div>
</template>

<script setup lang="ts">
import SfAlertError from '@/components/alerts/SfAlertError.vue'
import SfLargeButton from '@/components/button/SfLargeButton.vue'
import SfCheckbox from '@/components/input/SfCheckbox.vue'
import SfPasswordField from '@/components/input/SfPasswordField.vue'
import SfSelect from '@/components/input/SfSelect.vue'
import SfTextField from '@/components/input/SfTextField.vue'
import CustomTermsCheckbox from '@/modules/authentication/components/CustomTermsCheckbox.vue'
import LoginLink from '@/modules/authentication/components/LoginLink.vue'
import useRegistration from '@/modules/authentication/useRegistration'
import { getContactTitles } from '@/modules/user/helpers'
import rules from '@/rules'
import { InvitationRegistration, ContactTitle, ValidTokenDto } from '@/types/index'
import { storeToRefs } from 'pinia'
import { onBeforeMount, PropType, ref, useTemplateRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps({
  sending: {
    type: Boolean,
    required: true
  },
  tokenError: {
    type: Boolean,
    required: true
  },
  registrationData: {
    type: Object as PropType<ValidTokenDto>,
    default: undefined
  }
})

const emit = defineEmits(['finishRegistration'])

let locale = useI18n().locale.value
onBeforeMount(() => {
  locale = useI18n().locale ? useI18n().locale.value : navigator.language
  useRegistration().getCustomTerms(locale)
})

const titleItems = getContactTitles()
const defaultTitle = titleItems.find((titleItem) => titleItem.title === ContactTitle.UNSPECIFIED)!
const form = useTemplateRef('form')
const valid = ref(false)

const selectedTitle = ref(defaultTitle)
const firstName = ref('')
const lastName = ref('')
const password = ref('')
const repeat = ref('')
const termsConsent = ref(false)
const customTermsConsent = ref(false)

const { customTerms } = storeToRefs(useRegistration())

watch(
  () => props.registrationData,
  () => {
    selectedTitle.value =
      titleItems.find((item) => item.title == props.registrationData?.contactTitle) || defaultTitle
    firstName.value = props.registrationData?.firstName || ''
    lastName.value = props.registrationData?.lastName || ''
  }
)

const sendRegistration = async () => {
  if (form.value?.validate()) {
    const selectedContactTitle: ContactTitle =
      (titleItems[selectedTitle.value.value]?.title as ContactTitle) ?? ContactTitle.UNSPECIFIED
    const data: InvitationRegistration = {
      token: { value: '' },
      firstName: firstName.value,
      lastName: lastName.value,
      password: password.value,
      contactTitle: selectedContactTitle as ContactTitle,
      termsConfirmation: termsConsent.value,
      customTermsConfirmation: customTermsConsent.value,
      alpha2language: locale
    }

    emit('finishRegistration', data)
  }
}
</script>
